import { derived } from "svelte/store";
import { notify } from "ceyper-common";

import { COMMANDS } from "$lib/commands/commands";
import { file, frameIndex, frames, interaction } from "$lib/state";
import {
    animationPlayback,
    pause,
    play,
    seek,
    setLoop,
    toggleLoop,
    togglePlayback,
} from "$lib/animation/playback";

export function registerTimelineCommands() {
    const { add, destroy } = COMMANDS.registerActions("all");

    const hasMultipleFrames = (frames: unknown[] | null) => (frames?.length ?? 0) > 1;

    const isFileAvailable = derived([file, interaction], ([$file, $interaction]) => !!$file && !!$interaction);
    const isFrameCountGreaterThanOne = derived(frames, hasMultipleFrames);

    const isNotFirstFrame = derived(
        [frames, frameIndex],
        ([$frames, $frameIndex]) => hasMultipleFrames($frames) && ($frameIndex ?? 0) > 0
    );

    const isNotLastFrame = derived(
        [frames, frameIndex],
        ([$frames, $frameIndex]) =>
            hasMultipleFrames($frames) && ($frameIndex ?? 0) < ($frames?.length ?? 0) - 1
    );

    const canPlay = derived(
        [frames, animationPlayback],
        ([$frames, $playback]) => hasMultipleFrames($frames) && !$playback.playing
    );

    const canPause = derived(
        [frames, animationPlayback],
        ([$frames, $playback]) => hasMultipleFrames($frames) && $playback.playing
    );

    /// Navigation

    add("animation.load", previewFrame, isFileAvailable);
    add("animation.change-frame", changeFrame, isFileAvailable);

    add(
        "animation.first-frame",
        () => {
            changeFrame(0);
        },
        isNotFirstFrame
    );

    add(
        "animation.next-frame",
        () => {
            changeFrame(wrapFrame((frameIndex.value ?? 0) + 1));
        },
        isFrameCountGreaterThanOne
    );

    add(
        "animation.prev-frame",
        () => {
            changeFrame(wrapFrame((frameIndex.value ?? 0) - 1));
        },
        isFrameCountGreaterThanOne
    );

    add(
        "animation.last-frame",
        () => {
            changeFrame((frames.value?.length ?? 1) - 1);
        },
        isNotLastFrame
    );

    /// Playback

    add("animation.play", play, canPlay);
    add("animation.pause", pause, canPause);
    add("animation.toggle-playback", togglePlayback, isFrameCountGreaterThanOne);
    add("animation.toggle-loop", toggleLoop, isFileAvailable);
    add("animation.set-loop", setLoop, isFileAvailable);

    /// Modify

    add(
        "animation.add-frame",
        (target: number = frameIndex.value ?? 0) => {
            const insertAt = clampInsertFrame(target + 1);
            interaction.value.addFrame(undefined, insertAt);
            changeFrame(insertAt);
        },
        isFileAvailable
    );

    add(
        "animation.duplicate-frame",
        (target: number = frameIndex.value ?? 0) => {
            if (!isValidFrame(target)) return false;

            const copyIndex = target + 1;
            interaction.value.duplicateFrame(target);
            changeFrame(copyIndex);
        },
        isFileAvailable
    );

    add(
        "animation.remove-frame",
        (target: number = frameIndex.value ?? 0) => {
            const frameCount = frames.value?.length ?? 0;
            if (frameCount <= 1) {
                notify({
                    type: "message",
                    level: "warn",
                    id: "animation-remove-frame-error",
                    title: "Can't remove last frame",
                    message: "The last frame in an animation cannot be deleted.",
                });
                return false;
            }

            if (!isValidFrame(target)) return false;

            const nextActiveFrame = Math.min(target, frameCount - 2);
            interaction.value.removeFrame(target);
            changeFrame(nextActiveFrame);
        },
        isFileAvailable
    );

    add("animation.move-frame", moveFrame, isFrameCountGreaterThanOne);

    add(
        "animation.move-frame-left",
        () => moveFrame(frameIndex.value ?? 0, (frameIndex.value ?? 0) - 1, true),
        isNotFirstFrame
    );

    add(
        "animation.move-frame-right",
        () => moveFrame(frameIndex.value ?? 0, (frameIndex.value ?? 0) + 1, true),
        isNotLastFrame
    );

    add(
        "animation.set-frame-duration",
        (target: number = frameIndex.value ?? 0, duration?: number) => {
            if (duration === undefined) {
                duration = target;
                target = frameIndex.value ?? 0;
            }

            const normalizedDuration = normalizeDuration(duration);
            if (!isValidFrame(target) || normalizedDuration === null) return false;

            interaction.value.setFrameDuration(target, normalizedDuration);
        },
        isFileAvailable
    );

    add(
        "animation.set-all-frame-durations",
        (duration: number) => {
            const normalizedDuration = normalizeDuration(duration);
            if (normalizedDuration === null) return false;

            interaction.value.setAllFrameDurations(normalizedDuration);
        },
        isFileAvailable
    );

    function wrapFrame(frame: number) {
        const length = frames.value?.length ?? 0;
        if (length === 0) return 0;

        return (frame + length) % length;
    }

    function clampInsertFrame(frame: number) {
        const length = frames.value?.length ?? 0;
        return Math.max(0, Math.min(Math.trunc(frame), length));
    }

    function changeFrame(frame: number) {
        if (!isValidFrame(frame)) return false;

        interaction.value.setFrame(frame);
        seek(frame, false);
        interaction.value.redraw();
    }

    function previewFrame(frame: number) {
        if (!isValidFrame(frame)) return false;

        seek(frame);
    }

    function moveFrame(from: number, to: number, setActive = false) {
        if (from === to) return false;
        if (!isValidFrame(from) || !isValidFrame(to)) return false;

        interaction.value.moveFrame(from, to);

        if (setActive) {
            changeFrame(to);
        }
    }

    function isValidFrame(frame: number) {
        const length = frames.value?.length ?? 0;
        return Number.isInteger(frame) && frame >= 0 && frame < length;
    }

    function normalizeDuration(duration: number | undefined) {
        if (!duration || !Number.isFinite(duration) || duration <= 0) {
            return null;
        }

        return Math.trunc(duration);
    }

    return { destroy };
}
